import { AxiosRequestConfig, AxiosResponse } from 'axios';

import axiosClient, { remoteEnvBaseUrl } from './axiosClient';

export interface CrossEnvClient {
  env: string;
  get<T = unknown>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  post<T = unknown>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  put<T = unknown>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  delete<T = unknown>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
}

/**
 * Access Sync (CPT-1135) — a client scoped to a single TARGET env, browser-
 * direct to that env's public host. Same underlying mechanism
 * useTenantVersions / useEnvVersions already use for cross-env READS
 * (`remoteEnvBaseUrl` + the shared `axiosClient`, whose module-level
 * interceptors attach the Auth0 bearer and 401-handle a remote failure
 * without tearing down the home session — see `axiosClient.ts`) — extended
 * here to WRITES, which those two hooks never needed.
 *
 * Deliberately ALWAYS builds an absolute URL, even for the env this portal
 * happens to be served from — it does NOT take the `isLocal` shortcut
 * (relative URL) those hooks use for their "home" leg. Reason: a relative
 * `/api/...` URL is intercepted by `crossEnvRequestRouter`'s block (B),
 * which rewrites it based on the globally-SELECTED env (the top-bar
 * dropdown's `getCurrentEnvForApi()`) — state that has nothing to do with
 * which env Access Sync's SOURCE actually is. Since the dropdown defaults to
 * `dev01` on every portal (dev01 is the sole authoring env everywhere — see
 * `EnvContext.tsx`), a relative call here could silently be redirected to
 * dev01 while the operator thinks they're reading their own portal's data.
 * An absolute URL bypasses that router entirely (it only rewrites URLs
 * starting with `/api/`), so every `crossEnvClient(env)` call goes exactly
 * where its `env` argument says, regardless of dropdown state — the
 * correctness this feature needs, since applying a wrong-env snapshot as
 * "source" would write bad data into a target.
 *
 * Bypassing the router also means Access Sync gets NONE of its prod-write
 * belt (which only fires on relative URLs) — by design. Access Sync
 * enforces its OWN prod gate client-side (`prod_option` + a typed confirm —
 * see `AccessSyncPanel.tsx`), and the real authority is the TARGET env's own
 * backend: every route this hits requires `requireRole('super-admin')`
 * resolved against THAT env's `config.app_user` / `app_user_role` rows, so a
 * write only ever succeeds where the caller is actually a super-admin.
 */
export function crossEnvClient(env: string): CrossEnvClient {
  const url = (path: string): string => `${remoteEnvBaseUrl(env)}${path}`;
  const tagged = (config?: AxiosRequestConfig): AxiosRequestConfig =>
    ({ ...config, __crossEnv: env } as AxiosRequestConfig);

  return {
    env,
    get: (path, config) => axiosClient.get(url(path), tagged(config)),
    post: (path, body, config) => axiosClient.post(url(path), body, tagged(config)),
    put: (path, body, config) => axiosClient.put(url(path), body, tagged(config)),
    delete: (path, config) => axiosClient.delete(url(path), tagged(config)),
  };
}
